import React, {FC, useEffect, useMemo, useState} from 'react'
import {FieldArray, Formik} from 'formik'
import {Button, Modal} from 'react-bootstrap'
import {KTIcon} from '../../../../../_websoft/helpers'
import {FmkNalogiStavkeModel} from '../../../../models/FmkNaloziModel'
import {getStavkaInfo} from '../../../../api/radniNalozi'
import {getStavkaId} from './materijalHelpers'
import {StartMaterijalCard} from './StartMaterijalCard'

export type MaterijalTip = 'rolna' | 'traka'

export type ZaduzenjeModalData = {
    tip: MaterijalTip
    uposlenik: string
} | null

export type StartMaterijalRow = {
    RowId: number
    StavkaIds: string[]
    Provjeren: boolean
    Ucitavanje: boolean
    Raw: string
    MaterijalId: string
    Skenirano: number
    PotrebnaKolicina: number | ''
    PotrebneKolicinePoStavci: Record<string, number>
    NormativPoruka: string
}

export type StartMaterijalData = {
    tip: MaterijalTip | ''
    stavke: FmkNalogiStavkeModel['stavka']
    materijali: StartMaterijalRow[]
}

interface Props {
    naloga: string
    selectedStavka: FmkNalogiStavkeModel['stavka']
    onChange: (data: StartMaterijalData) => void
}

interface ChangeReporterProps {
    tip: MaterijalTip
    stavke: FmkNalogiStavkeModel['stavka']
    materijali: StartMaterijalRow[]
    onChange: (data: StartMaterijalData) => void
}

const MaterijalChangeReporter: FC<ChangeReporterProps> = ({tip, stavke, materijali, onChange}) => {
    useEffect(() => {
        onChange({tip, stavke, materijali})
    }, [materijali, onChange, stavke, tip])

    return null
}

let nextRowId = 0

const createRow = (): StartMaterijalRow => ({
    RowId: ++nextRowId,
    StavkaIds: [],
    Provjeren: false,
    Ucitavanje: false,
    Raw: '',
    MaterijalId: '',
    Skenirano: 0,
    PotrebnaKolicina: '',
    PotrebneKolicinePoStavci: {},
    NormativPoruka: '',
})

const Step3: FC<Props> = ({naloga, selectedStavka, onChange}) => {
    const [selectedType, setSelectedType] = useState<MaterijalTip | ''>('')
    const [stavkaInfo, setStavkaInfo] = useState<Record<string, any>>({})
    const [infoReady, setInfoReady] = useState(false)
    const [infoError, setInfoError] = useState('')
    const [infoRetry, setInfoRetry] = useState(0)
    const [zaduzenjeModal, setZaduzenjeModal] = useState<ZaduzenjeModalData>(null)
    const initialValues = useMemo(
        () => ({
            materijali: selectedStavka.length ? [createRow()] : [],
        }),
        [selectedStavka]
    )

    useEffect(() => {
        if (!selectedType) onChange({tip: '', stavke: selectedStavka, materijali: []})
    }, [onChange, selectedStavka, selectedType])

    useEffect(() => {
        let active = true
        setInfoReady(false)
        setInfoError('')

        const loadStavkaInfo = async () => {
            const ids = selectedStavka.map(getStavkaId).filter(Boolean)

            if (!ids.length) {
                setStavkaInfo({})
                return
            }

            try {
                const rows = await Promise.all(
                    ids.map(async (id) => {
                        const response = await getStavkaInfo({IdStavke: id})
                        return [String(id), response] as const
                    })
                )

                if (!active) return

                setInfoReady(true)
                setStavkaInfo(
                    rows.reduce<Record<string, any>>((acc, [id, response]) => {
                        acc[id] = response
                        return acc
                    }, {})
                )
            } catch (error: any) {
                if (active) {
                    setInfoError(error?.message ?? 'Greška pri učitavanju podataka o stavkama.')
                }
            }
        }

        loadStavkaInfo()

        return () => {
            active = false
        }
    }, [selectedStavka, infoRetry])

    return (
        <div className='w-100'>
            <Modal show={Boolean(zaduzenjeModal)} onHide={() => setZaduzenjeModal(null)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Stavka je vec zaduzena</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className='alert alert-danger mb-4 fw-bold'>
                        Stavka je vec Zaduzena. Potrebno je uraditi promet rolne, nakon toga moguce je ponovo zaduziti rolnu.
                    </div>
                    <div>
                        <div className='text-muted small mb-1'>Uposlenik koji je zaduzio rolnu ili traku</div>
                        <div className='fw-bolder fs-5'>{zaduzenjeModal?.uposlenik || '-'}</div>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant='primary' onClick={() => setZaduzenjeModal(null)}>
                        U redu
                    </Button>
                </Modal.Footer>
            </Modal>

            <div className='card shadow-sm mb-6'>
                <div className='card-body p-4 p-md-6'>
                    <div className='d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between gap-3 mb-4'>
                        <div>
                            <h2 className='fw-bolder text-dark mb-1'>Odaberi Materijal</h2>
                            <div className='text-muted'>Prvo skenirajte materijal, zatim provjerite odabrane stavke.</div>
                        </div>
                        <span className='badge badge-light-primary py-2 px-3'>Stavki: {selectedStavka.length}</span>
                    </div>

                    <div className='row g-3' data-kt-buttons='true'>
                        <div className='col-12 col-md-6'>
                            <input
                                type='radio'
                                className='btn-check'
                                name='start_materijal_tip'
                                id='start_materijal_rolna'
                                onChange={() => setSelectedType('rolna')}
                                checked={selectedType === 'rolna'}
                            />
                            <label
                                className='btn btn-outline btn-outline-dashed btn-outline-default w-100 p-4 text-start h-100'
                                htmlFor='start_materijal_rolna'
                            >
                                <div className='d-flex align-items-center justify-content-between'>
                                    <div>
                                        <div className='fw-bolder fs-3'>ROLNA</div>
                                        <div className='text-muted mt-1'>Skeniranje rolne za start naloga</div>
                                    </div>
                                    <i className={`fas fa-circle-check fs-2 ${selectedType === 'rolna' ? 'text-primary' : 'text-muted'}`} />
                                </div>
                            </label>
                        </div>

                        <div className='col-12 col-md-6'>
                            <input
                                type='radio'
                                className='btn-check'
                                name='start_materijal_tip'
                                id='start_materijal_traka'
                                onChange={() => setSelectedType('traka')}
                                checked={selectedType === 'traka'}
                            />
                            <label
                                className='btn btn-outline btn-outline-dashed btn-outline-default w-100 p-4 text-start h-100'
                                htmlFor='start_materijal_traka'
                            >
                                <div className='d-flex align-items-center justify-content-between'>
                                    <div>
                                        <div className='fw-bolder fs-3'>TRAKA</div>
                                        <div className='text-muted mt-1'>Skeniranje trake za start naloga</div>
                                    </div>
                                    <i className={`fas fa-circle-check fs-2 ${selectedType === 'traka' ? 'text-primary' : 'text-muted'}`} />
                                </div>
                            </label>
                        </div>
                    </div>

                    <details className='mt-4'>
                        <summary className='fw-bold text-primary py-3 cursor-pointer'>Pregled odabranih stavki ({selectedStavka.length}) · Nalog {naloga}</summary>
                        <div className='row g-3 mt-1'>
                            {selectedStavka.map((item: any, index) => (
                                <div key={String(getStavkaId(item))} className='col-12 col-md-6'>
                                    <div className='border rounded p-3 h-100 text-break'>
                                        <div className='fw-bold'>{index + 1}. {item.NazivArtikla || item.Naziv}</div>
                                        <div>Količina: {item.Kolicina} {item.Jm ?? item.IdJedMjere ?? '-'}</div>
                                        <div className='text-muted fs-7'>Šifra: {item.SifraArtikla ?? item.IdArtikla ?? '-'} · FMK: {item.fmkId ?? item.FmkId ?? '-'}</div>
                                        <div className='text-muted fs-7'>Norma: {Number(item.NormaMin ?? 0) > 0 ? 'DA' : 'NE'}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </details>
                </div>
            </div>

            {selectedType ? (
                <Formik
                    key={selectedType}
                    initialValues={initialValues}
                    enableReinitialize
                    validateOnChange={false}
                    onSubmit={() => undefined}
                >
                    {({values}) => {
                        return (
                            <div>
                                <MaterijalChangeReporter
                                    tip={selectedType}
                                    stavke={selectedStavka}
                                    materijali={values.materijali}
                                    onChange={onChange}
                                />
                                <div className='alert alert-primary'>
                                    <div className='fw-bold'>1. Skenirajte materijal. 2. Provjerite označene stavke.</div>
                                    Za trake se stavke automatski označavaju prema širini u nazivu. Odabir možete ručno izmijeniti.
                                </div>
                                {infoError && (
                                    <div className='alert alert-danger' role='alert'>
                                        {infoError}
                                        <button type='button' className='btn btn-sm btn-light ms-3' onClick={() => setInfoRetry((value) => value + 1)}>
                                            Pokušaj ponovo
                                        </button>
                                    </div>
                                )}
                                <FieldArray name='materijali'>
                                    {({push, remove}) => (
                                        <div className='d-flex flex-column gap-4'>
                                            {values.materijali.map((row, index) => (
                                                <StartMaterijalCard
                                                    key={row.RowId}
                                                    row={row}
                                                    index={index}
                                                    tip={selectedType}
                                                    stavke={selectedStavka}
                                                    stavkaInfo={stavkaInfo}
                                                    infoReady={infoReady}
                                                    onZaduzenje={setZaduzenjeModal}
                                                    onRemove={() => remove(index)}
                                                    canRemove={values.materijali.length > 1}
                                                />
                                            ))}
                                            <button
                                                type='button'
                                                className='btn btn-lg btn-light-primary w-100'
                                                onClick={() => push(createRow())}
                                            >
                                                <KTIcon iconName='plus' className='fs-2' />
                                                {selectedType === 'rolna' ? 'Dodaj rolnu' : 'Dodaj traku'}
                                            </button>
                                        </div>
                                    )}
                                </FieldArray>
                                {selectedStavka.some((stavka) => !values.materijali.some((row) =>
                                    row.MaterijalId && row.StavkaIds.includes(String(getStavkaId(stavka)))
                                )) && (
                                    <div className='alert alert-light border mt-4 text-break'>
                                        Stavke bez materijala neće biti pokrenute: {selectedStavka.filter((stavka) => !values.materijali.some((row) =>
                                            row.MaterijalId && row.StavkaIds.includes(String(getStavkaId(stavka)))
                                        )).map((stavka: any) => stavka.NazivArtikla ?? stavka.Naziv ?? getStavkaId(stavka)).join(', ')}
                                    </div>
                                )}
                            </div>
                        )
                    }}
                </Formik>
            ) : (
                <div className='alert alert-warning mb-0'>
                    <div className='fw-bolder mb-1'>Nije odabrano</div>
                    <div className='mb-0'>Odaberite ROLNA ili TRAKA da biste nastavili.</div>
                </div>
            )}
        </div>
    )
}

export {Step3}
